import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import SearchModal from './SearchModal';
import { getCartItemCount } from '../../utils/cart';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [cartCount, setCartCount] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Update cart count
  useEffect(() => {
    const updateCartCount = () => {
      setCartCount(getCartItemCount());
    };

    updateCartCount();
    window.addEventListener('cartUpdated', updateCartCount);
    return () => window.removeEventListener('cartUpdated', updateCartCount);
  }, []);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Products', path: '/products' },
    { name: 'Services', path: '/services' },
    { name: 'Blog', path: '/blog' },
    { name: 'About', path: '/about' },
  ];

  const handleCatalogClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsMobileMenuOpen(false);
    
    // Force navigation and reset
    if (location.pathname === '/products') {
      // If already on products page, trigger a reset via custom event
      window.dispatchEvent(new CustomEvent('resetCatalogFilter'));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      // Navigate to products page with reset flag
      navigate('/products', { state: { resetFilter: true, timestamp: Date.now() } });
    }
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-white shadow-md' : 'bg-white/80 backdrop-blur-md'
        }`}
      >
        <nav className="px-6 lg:px-12 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 cursor-pointer">
              <div className="w-10 h-10 flex items-center justify-center">
                <img
                  src="https://public.readdy.ai/ai/img_res/cbd236d0-51e4-4c4a-9b66-0593130ad616.png"
                  alt="Digital Business Logo"
                  className="w-full h-full object-contain"
                />
              </div>
              <span className="text-xl font-bold text-gray-900">Digital Business</span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-8">
              <Link to="/" className="text-gray-700 hover:text-primary transition-colors font-medium cursor-pointer">
                Home
              </Link>
              <Link to="/products" className="text-gray-700 hover:text-primary transition-colors font-medium cursor-pointer">
                Products
              </Link>
              <Link to="/services" className="text-gray-700 hover:text-primary transition-colors font-medium cursor-pointer">
                Services
              </Link>
              <Link to="/blog" className="text-gray-700 hover:text-primary transition-colors font-medium cursor-pointer">
                Blog
              </Link>
              <Link to="/about" className="text-gray-700 hover:text-primary transition-colors font-medium cursor-pointer">
                About
              </Link>
            </nav>

            {/* Right Actions */}
            <div className="flex items-center gap-4">
              <button 
                onClick={() => setIsSearchOpen(true)}
                className="w-10 h-10 flex items-center justify-center text-gray-700 hover:text-primary transition-colors cursor-pointer"
              >
                <i className="ri-search-line text-xl"></i>
              </button>
              <Link
                to="/checkout"
                className="relative w-10 h-10 flex items-center justify-center text-gray-700 hover:text-primary transition-colors cursor-pointer"
              >
                <i className="ri-shopping-cart-line text-xl"></i>
                {cartCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-primary text-white text-xs rounded-full flex items-center justify-center">
                    {cartCount}
                  </span>
                )}
              </Link>
              <button
                onClick={handleCatalogClick}
                className="hidden lg:flex items-center gap-2 px-6 py-2 bg-gradient-to-r from-primary to-accent-green text-white rounded-lg font-medium hover:shadow-lg transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-apps-line text-lg"></i>
                Catalog
              </button>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="lg:hidden w-10 h-10 flex items-center justify-center text-gray-700 cursor-pointer"
              >
                <i className={`ri-${isMobileMenuOpen ? 'close' : 'menu'}-line text-2xl`}></i>
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {isMobileMenuOpen && (
            <div className="lg:hidden mt-4 pb-4 border-t border-gray-200 pt-4">
              <div className="flex flex-col gap-4">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`text-sm font-medium transition-colors cursor-pointer ${
                      location.pathname === link.path
                        ? 'text-primary'
                        : 'text-gray-700 hover:text-primary'
                    }`}
                  >
                    {link.name}
                  </Link>
                ))}
                <button
                  onClick={handleCatalogClick}
                  className="flex items-center justify-center gap-2 px-6 py-2 bg-gradient-to-r from-primary to-accent-green text-white rounded-lg font-medium cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-apps-line text-lg"></i>
                  Catalog
                </button>
              </div>
            </div>
          )}
        </nav>
      </header>

      {/* Search Modal */}
      <SearchModal isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)} />
    </>
  );
};

export default Header;